import React, { useEffect, useReducer, useRef } from "react";
import type { TFunction } from "i18next";
import U2000SaveCancelDialog from "../../../u2000Core/components/dialog/U2000SaveCancelDialog";
import U2000Label from "../../../u2000Core/components/formControls/label/U2000Label";
import U2000InputText from "../../../u2000Core/components/formControls/input/U2000InputText";
import U2000InputCalendarCustom from "../../../u2000Core/components/formControls/input/U2000InputCalendarCustom";
import u2000FormState, { U2000FormStateEnumEditMode, U2000FormStateEnumState, type U2000FormState } from "../../../u2000Core/reducer/formReducer/U2000FormState";
import { u2000IntFormState, useU2000FormStateHandlers } from "../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { countU3105PayrollBiweeklyPeriodsForWizard, isUtcMondayFirstPayrollDay } from "../../U3105Service";

export interface U3105NewPayrollYearDialogProps {
  isOpen: boolean;
  /** Chaîne formulaire (`yyyy-MM-dd` ou ISO) — la génération n’utilise que la partie date civile UTC. */
  onApply: (yearCode: string, firstMondayYmd: string) => void;
  onCancel: () => void;
  t: TFunction;
  NS: string;
}

export type U3105NewPayrollYearDialogRow = {
  yearCode: string;
  firstMondayYmd: string;
};

function parseUtcDateOnly(value: string): Date | null {
  const head = value.trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(head)) return null;
  const [ys, ms, ds] = head.split("-");
  const y = Number(ys);
  const m = Number(ms);
  const d = Number(ds);
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return null;
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  const dt = new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return dt;
}

function emptyDialogRow(): U3105NewPayrollYearDialogRow {
  return { yearCode: "", firstMondayYmd: "" };
}

const dlgInitialFormState: U2000FormState<U3105NewPayrollYearDialogRow> = {
  ...u2000IntFormState,
  current: emptyDialogRow(),
  old: null as unknown as U3105NewPayrollYearDialogRow,
  state: U2000FormStateEnumState.EDIT,
  editMode: U2000FormStateEnumEditMode.EDIT,
};

const U3105NewPayrollYearDialog: React.FC<U3105NewPayrollYearDialogProps> = ({ isOpen, onApply, onCancel, t, NS }) => {
  const [dialogFormState, dialogFormDispatch] = useReducer(u2000FormState, dlgInitialFormState);
  const wasOpenRef = useRef(false);

  const dlgForm = useU2000FormStateHandlers<U3105NewPayrollYearDialogRow>({
    formState: dialogFormState,
    formStateDispatch: dialogFormDispatch,
    businessHandlers: {},
  });

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      dlgForm.setEditMode(U2000FormStateEnumEditMode.EDIT);
      dlgForm.setData(emptyDialogRow());
    }
    wasOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- réinitialiser à la transition fermé → ouvert uniquement
  }, [isOpen]);

  const row = dlgForm.getData();
  const y = row?.yearCode?.trim() ?? "";
  const firstRaw = row?.firstMondayYmd?.trim() ?? "";
  const parsed = parseUtcDateOnly(firstRaw);
  const isMonday = parsed !== null && isUtcMondayFirstPayrollDay(firstRaw);
  const periodCount = isMonday ? countU3105PayrollBiweeklyPeriodsForWizard(firstRaw, y) : 0;
  /* 27 débuts « naturels » possibles avant la borne : build n’en garde que 26 (pas de période 27). */
  const countOk = periodCount === 26 || periodCount === 27;
  const isValid = Boolean(y && y.length <= 50 && parsed !== null && isMonday && countOk);

  const handleClose = (action: "save" | "cancel") => {
    if (action === "cancel") {
      dlgForm.setData(emptyDialogRow());
      onCancel();
      return;
    }
    if (!y || y.length > 50 || !parsed || !isMonday || !countOk) return;
    onApply(y, firstRaw);
    dlgForm.setData(emptyDialogRow());
  };

  return (
    <U2000SaveCancelDialog
      isOpen={isOpen}
      title={t("lblNewPayrollYearDlgTitle", { ns: NS })}
      onClose={handleClose}
      isValid={isValid}
      width="max-w-lg"
      contentOverflowVisible
    >
      <div className="space-y-4 px-1 py-2">
        <p className="text-sm text-gray-600">{t("msgNewPayrollYearDlgHelp", { ns: NS })}</p>
        <div className="flex flex-col gap-1.5">
          <U2000Label htmlFor="yearCode" label={t("lblYearCode", { ns: NS })} required />
          <U2000InputText
            hideLabel
            formStateHandlers={dlgForm as never}
            fieldName="yearCode"
            label={t("lblYearCode", { ns: NS })}
            required
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <U2000InputCalendarCustom
            formStateHandlers={dlgForm as never}
            fieldName="firstMondayYmd"
            label={t("lblFirstPayrollMonday", { ns: NS })}
            required
            dateFormat="yyyy-MM-dd"
            popupZIndex={10050}
            popupPosition="auto"
          />
          {parsed !== null && !isMonday ? (
            <p className="text-sm text-red-600" role="alert">
              {t("errFirstMondayRequired", { ns: NS })}
            </p>
          ) : null}
          {isMonday && y.length > 0 && y.length <= 50 && !countOk ? (
            <p className="text-sm text-red-600" role="alert">
              {t("errPayrollPeriodCount26", { ns: NS, count: periodCount })}
            </p>
          ) : null}
        </div>
      </div>
    </U2000SaveCancelDialog>
  );
};

export default U3105NewPayrollYearDialog;
